import * as React from 'react';
import styled from '@emotion/styled';
import * as vars from '../consts/variables';
import { scrollElementIntoView } from '../helpers/scroll';
import { TestSelector } from '../helpers/TestSelector';
import { __ } from '../helpers/TranslationService';
import { MuiLikeStyledLabel } from '../pages/RequestForm/components/Input';

const RequiredFieldError = styled.span`
    color: ${({ theme }) => theme.text.color.error};
    font-size: 12px;
    line-height: 18px;
    margin-top: 8px;
    font-weight: 600;
    display: block;
`;

const stylesDefault: Record<string, React.CSSProperties> = {
    wrapper: {
        margin: '0 15px 20px',
        position: 'relative',
    },
    title: {
        fontSize: 15,
        color: vars.darkGray,
        position: 'relative',
        background: vars.white,
        display: 'inline-block',
        marginLeft: 10,
        zIndex: 1,
        padding: '0 5px',
    },
    selectWrapper: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        overflow: 'hidden',
        position: 'relative',
        marginTop: -8,
        border: `1px solid ${vars.gray}`,
    },
    select: {
        fontSize: 16,
        width: '100%',
        padding: '10px 25px 10px 10px',
        opacity: 1,
        border: 'none',
        color: vars.black,
        background: vars.transparent,
    },
    arrowDown: {
        borderLeft: '8px solid transparent',
        borderRight: '8px solid transparent',
        borderTop: `8px solid ${vars.darkGray}`,
        position: 'absolute',
        right: 8,
        top: '40%',
        pointerEvents: 'none',
    },
};

const stylesMui: Record<string, React.CSSProperties> = {
    ...stylesDefault,
    select: {
        ...stylesDefault.select,
        background: '#fff',
        borderRadius: '8px',
        height: '48px',
        border: `2px solid ${vars.gray}`,
    },
    selectWrapper: {
        ...stylesDefault.selectWrapper,
        borderRadius: '8px',
        border: 'none',
    },
};

const StyledLabel = styled.div`
    position: relative;
    display: inline-block;
    z-index: 1;

    ${MuiLikeStyledLabel}
`;

interface IProps {
    title?: string;
    onChange: (index: number | string) => void;
    options: { id: number | string; label: string }[];
    selectedId: number | string;
    defaultOption?: string;
    isError?: boolean;
    wrapperStyle?: any;
    cabins?: any;
    isFirstInvalidFormField?: boolean;
    testSelectorName?: string;
    className?: string;
    stringValues?: boolean;
    muiStyling?: boolean;
}

export const getOptions = (options) =>
    options.map((option, idx) => (
        <option key={`${idx}-${option.id}`} value={option.id}>
            {option.label}
        </option>
    ));

export const getCabinOptions = (cabinsObj) => {
    return [
        {
            key: 'inside',
            translationString: 'Inside Cabin',
        },
        {
            key: 'outside',
            translationString: 'Outside Cabin',
        },
        {
            key: 'balcony',
            translationString: 'Balcony Cabin',
        },
        {
            key: 'suite',
            translationString: 'Suite',
        },
        {
            key: 'withoutPrice',
            translationString: 'not available',
        },
    ]
        .filter((item) => cabinsObj[item.key]?.cabins.length > 0)
        .map((item) => (
            <optgroup label={__(item.translationString, 'dreamlines')} key={item.key}>
                {getOptions(cabinsObj[item.key].cabins)}
            </optgroup>
        ));
};

export class NativeSelect extends React.Component<IProps> {
    selectRef = null;
    getSelectRef = (ref) => (this.selectRef = ref);

    componentDidUpdate() {
        if (this.props.isFirstInvalidFormField && this.props.isError) {
            scrollElementIntoView(this.selectRef);
        }
    }

    handleChange = (event) => {
        const intValue = !this.props.stringValues
            ? parseInt(event.target.value, 10)
            : event.target.value;
        const value = intValue !== -1 ? intValue : null;
        this.props.onChange(value);
    };

    render() {
        const errorStateStyles: React.CSSProperties = {
            borderColor: this.props.isError ? vars.pink : vars.gray,
        };

        const styles: typeof stylesDefault = this.props.muiStyling
            ? {
                  ...stylesMui,
                  select: {
                      ...stylesMui.select,
                      ...errorStateStyles,
                  },
              }
            : {
                  ...stylesDefault,
                  selectWrapper: {
                      ...stylesDefault.selectWrapper,
                      ...errorStateStyles,
                      marginTop: this.props.title ? -8 : 0,
                  },
              };

        const wrapperMargin: any = {
            marginBottom: this.props.title ? 15 : 20,
        };

        const isNothingSelected =
            this.props.selectedId === -1 || (!this.props.selectedId && this.props.selectedId !== 0);

        const selectStyles = isNothingSelected
            ? { ...styles.select, color: vars.mediumBlack }
            : styles.select;

        const isSingleSail = this.props.options.length === 1 && !this.props.defaultOption;
        const Options = isSingleSail ? (
            <div style={styles.select}>{this.props.options[0].label}</div>
        ) : (
            <select
                onChange={this.handleChange}
                value={this.props.selectedId}
                style={{ ...selectStyles, ...errorStateStyles }}
                {...TestSelector.getSelector(this.props.testSelectorName)}
            >
                {this.props.defaultOption && <option value={-1}>{this.props.defaultOption}</option>}
                {this.props.cabins
                    ? getCabinOptions(this.props.cabins)
                    : getOptions(this.props.options)}
            </select>
        );
        return (
            <div
                className={this.props.className}
                ref={this.getSelectRef}
                style={{ ...styles.wrapper, ...wrapperMargin, ...this.props.wrapperStyle }}
            >
                {this.props.title && this.props.muiStyling && (
                    <StyledLabel>{this.props.title}</StyledLabel>
                )}

                {this.props.title && !this.props.muiStyling && (
                    <div style={styles.title}>{this.props.title}</div>
                )}
                <div style={styles.selectWrapper}>
                    {!isSingleSail && <span style={styles.arrowDown} />}
                    {Options}
                </div>

                {this.props.isError && (
                    <RequiredFieldError>{__('requiredField', 'dreamlines')}</RequiredFieldError>
                )}
            </div>
        );
    }
}
